/**
 * ArRowContextMenu Component
 *
 * Provides a right-click context menu for ArTable rows: annotation, lock,
 * bundle, duplicate, insert, delete row actions plus cell actions (Copy Cell,
 * Paste Cell, Switch Cell Mode) that act on the right-clicked cell — the
 * table body's onContextMenuCapture focuses that cell before this menu opens.
 * Cell actions resolve all state at click time (non-reactive proxy reads), so
 * the menu adds no subscriptions and no render-path cost.
 *
 * Copy Rows and Paste Rows come off `ArTableClipboardContext` (SYD-2314) rather
 * than a second implementation, anchored on THIS row instead of the focused
 * cell's — the row the user right-clicked is the one the menu speaks for. The
 * context carries the single instance `ArTableProvider` builds, so a page of
 * rows holds one clipboard hook rather than one per menu.
 *
 * Grouping consumes the selection, so it ends it through the shared
 * `clearSelection` — the one writer that also returns `scope` to `'visible'`
 * (SYD-2386).
 *
 * Delete asks nothing and runs straight away: `useRowDeleteTargets` routes it
 * through the callable, whose trash the undo buttons reverse by id (SYD-2447).
 * The remaining `MODAL_COPY` entries belong to the actions that have no way back.
 */

import { FC, useState, ReactNode, useId, useContext } from 'react';
import { Modal, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined,
  CommentOutlined,
  CloseOutlined,
  LockOutlined,
  UnlockOutlined,
  CopyOutlined,
  PlusOutlined,
  DisconnectOutlined,
  GroupOutlined,
  AppstoreOutlined,
  SnippetsOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { snapshot } from 'valtio';
import {
  ArTableActionsContext,
  ArTableBundleContext,
  ArTableClipboardContext,
  ArTableUIStateContext,
} from '../context';
import { useRowDeleteTargets } from './useRowDeleteTargets';
import { ContextMenu } from '@/providers/ContextMenu';
import { clearSelection } from '@/workflows/runtime/arTableSelection';
import { MessageContext } from '@/providers/MessageContext';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import {
  buildCopyPayload,
  computePasteValues,
  parseClipboard,
  pasteSummary,
  readSystemClipboard,
  resolveColumn,
  writeSystemClipboard,
} from '../clipboard';
import { createArFormulaEngineConfig } from '#types';
import { isEntityReference, isManualEntryValue } from '#types/System.Artifacts.types';
import type { ArBundleType, ArRowData, ArRowId, TypeId } from '#types/System.Artifacts.types';
import { useStoreNonReactive } from '@/store';
import type { CollectionState, DocumentState } from '@/store';
import type { ArTableType } from '#types/System.Artifacts.types';

const { Text } = Typography;

type ArRowContextMenuProps = {
  rowId: ArRowId;
  isLocked: boolean;
  annotation: string | undefined;
  disabled?: boolean;
  children: ReactNode;
  bundleRole?: 'member';
  bundleId?: string;
  bundleType?: ArBundleType;
};

type ModalState = 'remove-annotation' | 'switch-to-reference' | 'switch-to-manual' | null;

const MODAL_COPY: Record<Exclude<ModalState, null>, { title: string; okText: string; ariaLabel: string; body: string }> = {
  'remove-annotation': {
    title: 'Remove Annotation',
    okText: 'Remove',
    ariaLabel: 'Remove annotation confirmation',
    body: 'Are you sure you want to remove the annotation from this row?',
  },
  'switch-to-reference': {
    title: 'Switch to Record Reference',
    okText: 'Switch',
    ariaLabel: 'Switch to reference confirmation',
    body: 'Switching to record reference will erase this cell value.',
  },
  'switch-to-manual': {
    title: 'Switch to Manual Entry',
    okText: 'Switch',
    ariaLabel: 'Switch to manual entry confirmation',
    body: 'Switching to manual entry will erase this cell value.',
  },
};

/**
 * ArRowContextMenu wraps row content and provides right-click context menu.
 */
const ArRowContextMenu: FC<ArRowContextMenuProps> = ({
  rowId,
  isLocked,
  annotation,
  disabled = false,
  children,
  bundleRole,
  bundleId,
  bundleType,
}) => {
  const menuId = useId();
  const uiState = useContext(ArTableUIStateContext)!;
  const actions = useContext(ArTableActionsContext)!;
  const bundleCtx = useContext(ArTableBundleContext);
  const { messageApi } = useContext(MessageContext);
  const { fieldTypes } = useContext(FieldTypeContext);
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const { state: rowsStateRaw } = useStoreNonReactive('rows');
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;
  const groupsEnabled = schemaState.data?.bundles?.groups === true;
  const kitsEnabled = schemaState.data?.bundles?.kits?.enabled === true;
  const bundlesEnabled = groupsEnabled || kitsEnabled;
  const [modalState, setModalState] = useState<ModalState>(null);
  const [adding, setAdding] = useState(false);
  const deleteTargets = useRowDeleteTargets(rowId, isLocked);
  const { copyRowsToClipboard, pasteRowsFromClipboard } = useContext(ArTableClipboardContext)!;

  const hasAnnotation = annotation !== undefined;

  const modalCopy = modalState === null ? null : MODAL_COPY[modalState];

  const focusedCellColumnId = (): TypeId | null => {
    const focused = snapshot(uiState).cellFocus.focusedCell;
    if (!focused || focused.rowId !== rowId) return null;
    return focused.columnId as TypeId;
  };

  const handleModalOk = () => {
    if (modalState === 'remove-annotation') {
      actions.updateAnnotation(rowId, undefined);
    } else if (modalState === 'switch-to-reference') {
      const columnId = focusedCellColumnId();
      if (columnId) {
        void actions.pasteDownColumn(rowId, columnId, [null], { overflow: 'stop' });
      }
    } else if (modalState === 'switch-to-manual') {
      const columnId = focusedCellColumnId();
      if (columnId) {
        void actions.pasteDownColumn(rowId, columnId, [{ valueType: 'manual', value: '' }], { overflow: 'stop' });
      }
    }
    setModalState(null);
  };

  const handleModalCancel = () => {
    setModalState(null);
  };

  const handleAddAnnotation = () => {
    actions.updateAnnotation(rowId, '');
  };

  const handleCopyCell = async () => {
    const columnId = focusedCellColumnId();
    if (!columnId) {
      messageApi.info('Right-click a cell to copy it.');
      return;
    }
    const target = resolveColumn(schemaState.data, fieldTypes, columnId);
    if (!target) {
      messageApi.info('This column cannot be copied.');
      return;
    }
    const uiSnap = snapshot(uiState);
    const { plain, html } = buildCopyPayload({
      focused: { rowId, columnId },
      selection: uiSnap.selection,
      rendered: (uiSnap.renderedRowIds ?? []) as ArRowId[],
      target,
      getRowData: (rid) => rowsState.docs.get(rid)?.data,
      formulaConfig: createArFormulaEngineConfig(schemaState.data ?? undefined),
    });
    messageApi.info(
      (await writeSystemClipboard({ plain, html })) ? 'Copied' : 'Clipboard access was blocked.'
    );
  };

  const handlePasteCell = async () => {
    const columnId = focusedCellColumnId();
    if (!columnId) {
      messageApi.info('Right-click a cell to paste into it.');
      return;
    }
    const target = resolveColumn(schemaState.data, fieldTypes, columnId);
    if (!target?.pasteable) {
      messageApi.info('This column cannot be pasted into.');
      return;
    }
    const payload = await readSystemClipboard();
    if (payload === null) {
      messageApi.info('Clipboard access was blocked.');
      return;
    }
    const parsed = parseClipboard(payload);
    if (target.kind === 'reference' && !target.allowManual && parsed.kind === 'external') {
      messageApi.info('This column does not allow manual entry.');
      return;
    }
    const values = computePasteValues(parsed, target);
    if (values.length === 0) {
      messageApi.info('Nothing to paste.');
      return;
    }
    const result = await actions.pasteDownColumn(rowId, columnId, values, { overflow: 'createRows' });
    const summary = pasteSummary(result);
    if (summary) messageApi.info(summary);
  };

  const handleSwitchCellMode = () => {
    const columnId = focusedCellColumnId();
    if (!columnId) {
      messageApi.info('Right-click a cell to switch its entry mode.');
      return;
    }
    const target = resolveColumn(schemaState.data, fieldTypes, columnId);
    if (!target || target.kind !== 'reference') {
      messageApi.info('Entry mode applies to reference columns.');
      return;
    }
    if (!target.pasteable) {
      messageApi.info('This column cannot be edited.');
      return;
    }
    const value = rowsState.docs.get(rowId)?.data?.cells?.[columnId]?.value ?? null;
    if (isManualEntryValue(value)) {
      setModalState('switch-to-reference');
      return;
    }
    if (!target.allowManual) {
      messageApi.info('This column does not allow manual entry.');
      return;
    }
    const cacheLabel = isEntityReference(value) ? value.cacheLabel : undefined;
    const preservedLabel = cacheLabel && cacheLabel.trim() !== '' ? cacheLabel : undefined;
    if (value !== null && !preservedLabel) {
      setModalState('switch-to-manual');
      return;
    }
    void actions.pasteDownColumn(
      rowId,
      columnId,
      [{ valueType: 'manual', value: preservedLabel ?? '' }],
      { overflow: 'stop' }
    );
  };

  const handleAddRowAfter = async () => {
    setAdding(true);
    try {
      if (bundleRole === 'member' && bundleId) {
        await actions.addMemberRow(bundleId, rowId);
      } else {
        await actions.addRow(undefined, { afterRowId: rowId });
      }
    } finally {
      setAdding(false);
    }
  };

  const handleAddRowAsUngrouped = async () => {
    setAdding(true);
    try {
      await actions.addRow(undefined, { afterRowId: rowId });
    } finally {
      setAdding(false);
    }
  };

  const handleCreateBundle = async (type: 'group' | 'kit') => {
    const renderedRowIds = uiState.renderedRowIds;
    const selectedRows = uiState.selection.selectedRows;
    const selectedInOrder = renderedRowIds.filter((id) => selectedRows[id] === true) as ArRowId[];
    await actions.createBundle(selectedInOrder, type);
    if (selectedInOrder.length > 0) {
      clearSelection(uiState.selection);
    }
  };

  const bundleTypeLabel = bundleType === 'kit' ? 'Kit' : 'Group';
  const selectedCount = uiState.selection.selectedCount;

  const moveToBundleChildren: NonNullable<MenuProps['items']> = bundleCtx
    ? Array.from(bundleCtx.bundleMap.entries())
      .filter(([id]) => id !== bundleId)
      .map(([id, entry]) => ({
        key: `move-to-${id}`,
        label: `${entry.bundleType === 'kit' ? 'Kit' : 'Group'}: ${entry.label}`,
        onClick: () => actions.moveToBundle(rowId, id),
        disabled,
      }))
    : [];

  const bundleItems: NonNullable<MenuProps['items']> = (() => {
    if (bundleCtx && bundleRole === 'member') {
      return [
        { type: 'divider' as const },
        {
          key: 'add-member',
          label: 'Add Member Below',
          icon: <PlusOutlined />,
          onClick: handleAddRowAfter,
          disabled: disabled || adding,
        },
        {
          key: 'remove-from-bundle',
          label: `Remove from ${bundleTypeLabel}`,
          icon: <DisconnectOutlined />,
          onClick: () => actions.removeFromBundle(rowId),
          disabled,
        },
        ...(moveToBundleChildren.length > 0 ? [{
          key: 'move-to-bundle',
          label: 'Move to...',
          children: moveToBundleChildren,
          disabled,
        }] : []),
      ];
    }

    if (bundlesEnabled && !bundleRole) {
      const items: NonNullable<MenuProps['items']> = [
        { type: 'divider' as const },
      ];
      if (groupsEnabled) {
        items.push({
          key: 'create-group',
          label: selectedCount > 0 ? 'Create Group from Selection' : 'Create Group',
          icon: <GroupOutlined />,
          onClick: () => handleCreateBundle('group'),
          disabled,
        });
      }
      if (kitsEnabled) {
        items.push({
          key: 'create-kit',
          label: selectedCount > 0 ? 'Create Kit from Selection' : 'Create Kit',
          icon: <AppstoreOutlined />,
          onClick: () => handleCreateBundle('kit'),
          disabled,
        });
      }
      return items;
    }

    return [];
  })();

  const items: MenuProps['items'] = [
    !hasAnnotation && {
      key: 'add-annotation',
      label: 'Add Annotation',
      icon: <CommentOutlined />,
      onClick: handleAddAnnotation,
      disabled: disabled || isLocked,
    },
    hasAnnotation && {
      key: 'remove-annotation',
      label: 'Remove Annotation',
      icon: <CloseOutlined />,
      onClick: () => setModalState('remove-annotation'),
      disabled: disabled || isLocked,
    },
    { type: 'divider' as const },
    !isLocked && {
      key: 'lock',
      label: 'Lock Row',
      icon: <LockOutlined />,
      onClick: () => actions.lockRow(rowId, true),
      disabled: disabled,
    },
    isLocked && {
      key: 'unlock',
      label: 'Unlock Row',
      icon: <UnlockOutlined />,
      onClick: () => actions.lockRow(rowId, false),
      disabled: disabled,
    },
    ...bundleItems,
    { type: 'divider' as const },
    {
      key: 'copy-cell',
      label: 'Copy Cell',
      icon: <CopyOutlined />,
      onClick: () => void handleCopyCell(),
      disabled: disabled,
    },
    {
      key: 'paste-cell',
      label: 'Paste Cell',
      icon: <SnippetsOutlined />,
      onClick: () => void handlePasteCell(),
      disabled: disabled || isLocked,
    },
    {
      key: 'switch-cell-mode',
      label: 'Switch Cell Mode',
      icon: <SwapOutlined />,
      onClick: handleSwitchCellMode,
      disabled: disabled || isLocked,
    },
    { type: 'divider' as const },
    {
      key: 'copy-rows',
      label: 'Copy Rows',
      icon: <CopyOutlined />,
      onClick: () => void copyRowsToClipboard(rowId),
      disabled: disabled,
    },
    {
      key: 'paste-rows',
      label: 'Paste Rows',
      icon: <SnippetsOutlined />,
      onClick: () => void pasteRowsFromClipboard(rowId),
      disabled: disabled,
    },
    { type: 'divider' as const },
    {
      key: 'duplicate',
      label: 'Duplicate Row',
      icon: <CopyOutlined />,
      onClick: () => actions.duplicateRow(rowId),
      disabled: disabled,
    },
    bundleCtx && bundleRole === 'member'
      ? {
        key: 'add-row',
        label: 'Insert Row Below',
        icon: <PlusOutlined />,
        disabled: disabled || adding,
        children: [
          {
            key: 'add-member-below',
            label: 'As Member',
            onClick: handleAddRowAfter,
            disabled: disabled || adding,
          },
          {
            key: 'add-ungrouped-below',
            label: 'As Ungrouped',
            onClick: handleAddRowAsUngrouped,
            disabled: disabled || adding,
          },
        ],
      }
      : {
        key: 'add-row',
        label: 'Insert Row Below',
        icon: <PlusOutlined />,
        onClick: handleAddRowAfter,
        disabled: disabled || adding,
      },
    {
      key: 'delete',
      /**
       * A disabled action that says why. An antd Tooltip, not the native `title`
       * attribute: the browser's own tooltip renders outside the document, so
       * neither a test nor a verification pass can see whether the reason is there
       * at all — and one that cannot be observed was, in fact, not being shown.
       * The wrapper carries no `aria-label`: it would become the menu item's own
       * accessible name and rename the action.
       */
      label: deleteTargets.disabledReason === null
        ? deleteTargets.label
        : (
          <Tooltip title={deleteTargets.disabledReason} placement="left">
            <Text>{deleteTargets.label}</Text>
          </Tooltip>
        ),
      icon: <DeleteOutlined />,
      onClick: () => { void deleteTargets.deleteRows(); },
      disabled: disabled || deleteTargets.disabled,
      danger: true,
    },
  ].filter(Boolean) as MenuProps['items'];

  return (
    <>
      <ContextMenu
        menuId={menuId}
        items={items}
        disabled={disabled}
        onOpenChange={deleteTargets.capture}
      >
        {children}
      </ContextMenu>

      <Modal
        title={modalCopy?.title}
        open={modalState !== null}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={modalCopy?.okText}
        cancelText="Cancel"
        aria-label={modalCopy?.ariaLabel}
      >
        {modalCopy && <Text>{modalCopy.body}</Text>}
      </Modal>
    </>
  );
};

export default ArRowContextMenu;
